/* Copyright (c) 2020-present CloudIO Inc */

import {
  atom,
  useStateXAddToUndo,
  useStateXAtom,
  useStateXAtomSetter,
  useStateXAtomValue,
  useStateXRevertUndo,
} from '@cloudio-saas/statex';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  PaperProps,
} from '@mui/material';
import {
  AliasAndEmbeddedPageIdValue,
  AnyItem,
  ExpressionValue,
  getManager,
  getName,
  Item,
  Page,
  Platform,
  PropertyDefinition,
} from 'componentregistry';
import { getController } from 'controller/ControllerRegistry';
import { designPath, pageAtom } from 'designer/atoms';
import { useDragControls } from 'framer-motion';
import useWindowSize, { rootPageWidthAtom } from 'hooks/useWindowSize';
import Icon from 'icons/Icon';
import { DraggablePaperComponent } from 'platform/ExamineItemProps';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { itemWithDefaultValues } from 'registry/Components';
import { stopPropagation } from 'utils/events';
import { getErrorMessage, isEmpty } from 'utils/isEmpty';
import zIndex from '../../../../constants/ZIndex';
import {
  getPropertyDef,
  isRequired,
} from '../PropertiesView/property-editors/PropertyEditorUtils';
import {
  AnalyzeIssue,
  getDataStoreItem,
  makeAlert,
  severityToInt,
} from './AnalyzeUtils';

function isPropertyEmpty(def: PropertyDefinition, value: unknown): boolean {
  if (isEmpty(value)) {
    return true;
  }
  switch (def.type) {
    case 'expression':
    case 'ColorExpression':
      return typeof value === 'string'
        ? isEmpty(value)
        : isEmpty((value as ExpressionValue).value);
    case 'booleanExpression':
      return (
        typeof value !== 'boolean' && isEmpty((value as ExpressionValue).value)
      );
    case 'MultiItemID':
      return !Array.isArray(value) || value.length === 0;
  }
  return false;
}

export async function analyzeItem({
  appUid,
  page,
  pageId,
  platform,
  item,
  alerts: all_alerts,
  autoFixes: all_autoFixes,
}: {
  appUid: string;
  pageId: string;
  platform: Platform;
  page: Page;
  item: AnyItem;
  alerts: AnalyzeIssue[];
  autoFixes: (() => void)[];
}) {
  if (!item) return;
  const mgr = getManager(item.type);
  if (!mgr) return;

  const alerts: AnalyzeIssue[] = [];
  const autoFixes: (() => void)[] = [];

  const { deprecated, fixDeprecated } = mgr;
  if (deprecated) {
    let fix = undefined;
    if (fixDeprecated) {
      fix = () => {
        fixDeprecated(item, {
          appUid,
          pageId,
          platform,
          mgr,
          itemId: item.itemId,
        });
      };
      autoFixes.push(fix);
    }
    alerts.push({
      key: `deprecated-${item.itemId}`,
      showItemId: item.itemId,
      alert: `${mgr.name} is deprecated! ${deprecated}!`,
      itemId: item.itemId,
      severity: 'warning',
      fix,
    });
  }

  if (item.itemId !== 'root') {
    const parent = page[item.parent];
    if (!parent) {
      alerts.push({
        key: `missing-parent-${item.itemId}`,
        showItemId: item.itemId,
        alert: `Missing parent with itemId ${item.parent}!`,
        itemId: item.itemId,
      });
    } else {
      const parentMgr = getManager(parent.type);
      const children = parent.children.filter((c) => c !== item.itemId);
      const reason = mgr.isAllowedInsideParent(
        page,
        { ...parent, children },
        {
          appUid,
          itemId: item.itemId,
          pageId,
          mgr,
          platform,
        },
      );
      if (reason !== true) {
        alerts.push({
          key: `not-allowed-inside-parent-${item.itemId}`,
          showItemId: item.itemId,
          alert: `${mgr.name} is not allowed inside ${parentMgr.name}!${
            typeof reason === 'string' ? ` Reason: ${reason}` : ''
          }`,
          itemId: item.itemId,
        });
      }
    }
  }

  item.children.forEach((childId) => {
    const child = page[childId];
    if (!child) {
      const fix = () => {
        const _page = { ...platform.getPage(appUid, pageId) };
        const _item = { ..._page[item.itemId] };
        _item.children = _item.children.filter((c) => c !== childId);
        _page[item.itemId] = _item;
        platform.setter(pageAtom, _page, {
          params: { appUid, pageId },
        });
      };
      alerts.push({
        key: `missing-child-${item.itemId}`,
        showItemId: item.itemId,
        alert: `Missing child with itemId ${childId}!`,
        itemId: item.itemId,
        fix,
        tip: 'Remove orphan child',
      });
    } else {
      const childMgr = getManager(child.type);
      if (!childMgr) {
        alerts.push({
          key: `missing-manager-${child.itemId}`,
          showItemId: child.itemId,
          alert: `Unknown component type ${child.type} for item with ID ${child.itemId}!`,
          itemId: child.itemId,
        });
      } else {
        const children = item.children.filter((c) => c !== childId);
        const reason = mgr.isChildAllowed(
          page,
          { ...item, children },
          child.type,
          {
            appUid,
            itemId: item.itemId,
            pageId,
            mgr: mgr,
            platform,
          },
        );
        if (reason !== true) {
          alerts.push({
            key: `not-allowed-inside-child-${item.itemId}`,
            showItemId: item.itemId,
            alert: `${childMgr.name} is not allowed inside ${mgr.name}!${
              typeof reason === 'string' ? ` Reason: ${reason}` : ''
            }`,
            itemId: item.itemId,
          });
        }
      }
    }
  });

  const propKeys = Object.keys(mgr.props);
  await Promise.all(
    propKeys.map(async (prop) => {
      const _item = itemWithDefaultValues(item) as Item<never>;
      const itemId = _item.itemId;
      const value = _item.props[prop];
      const def = getPropertyDef(mgr, prop);

      const visible = mgr.isPropertyVisible(prop, _item.props, {
        appUid,
        itemId,
        pageId,
        mgr,
        platform,
      });

      if (visible) {
        if (isPropertyEmpty(def, value)) {
          if (
            isRequired({
              platform,
              appUid,
              pageId,
              itemId,
              mgr,
              required: def.required,
            })
          ) {
            alerts.push({
              key: `incomplete-${itemId}`,
              showItemId: itemId,
              alert: `Incomplete ${mgr.name}. Value for '${def.label}' property is not defined!`,
              itemId,
            });
          }
        } else if (def.type === 'ItemID') {
          if (!page[value]) {
            alerts.push({
              key: `wrong-id-${itemId}`,
              showItemId: itemId,
              alert: `${def.label} [${value}], used in ${mgr.name}, not found in the page!`,
              itemId,
            });
          } else {
            const { validateItemId } = def;
            if (validateItemId) {
              const msg = validateItemId(value, {
                pageId,
                appUid,
                itemId,
                mgr,
                platform,
              });
              if (msg) {
                alerts.push({
                  key: `invalid-itemid-${itemId}`,
                  showItemId: itemId,
                  alert: msg,
                  itemId,
                });
              }
            }
          }
        } else if (def.type === 'DataSourceAliasChooser') {
          const dsItem = getDataStoreItem({ page, alias: value });
          if (!dsItem) {
            alerts.push({
              key: `missing-alias-${itemId}`,
              showItemId: itemId,
              alert: `Missing data store for alias ${value}!`,
              itemId,
            });
          } else if (!def.allowedTypes.includes(dsItem.type)) {
            alerts.push({
              key: `wrong-type-${itemId}`,
              showItemId: itemId,
              alert: `${def.label} with value [${value}] of type [${getName(
                dsItem.type,
              )}] is not allowed. Allowed types are [${def.allowedTypes
                .map((t) => getName(t))
                .join(', ')}]`,
              itemId,
            });
          }
        } else if (def.type === 'controllerFunction') {
          const controller = getController(appUid, pageId);
          if (!controller || typeof controller[value] !== 'function') {
            alerts.push({
              key: `missing-function-${itemId}`,
              showItemId: itemId,
              alert: `Missing controller function ${value}!`,
              itemId,
            });
          }
        } else if (def.type === 'ViewAttributeChooser') {
          const { aliasType, getAlias } = def;
          let aliasAndEmbeddedPageId: AliasAndEmbeddedPageIdValue | null = null;
          if (getAlias) {
            aliasAndEmbeddedPageId = getAlias(page, item, {
              pageId,
              appUid,
              itemId,
              mgr,
              platform,
            });
          } else {
            const _aliasType =
              typeof aliasType === 'function'
                ? aliasType({ mgr, appUid, pageId, platform, itemId })
                : aliasType;
            aliasAndEmbeddedPageId = platform.getDataSourceAlias(
              appUid,
              pageId,
              itemId,
              _aliasType,
            );
          }

          if (!aliasAndEmbeddedPageId?.alias) {
            alerts.push({
              key: `missing-alias-${itemId}`,
              showItemId: itemId,
              alert: `Missing parent alias for ${mgr.name}'s [${def.label}] prop with value [${value}]!`,
              itemId,
            });
          } else {
            const store = platform.getStore(
              appUid,
              aliasAndEmbeddedPageId.embeddedPageId ?? pageId,
              aliasAndEmbeddedPageId.alias,
            );
            if (!store) {
              alerts.push({
                key: `missing-store-${itemId}`,
                showItemId: itemId,
                alert: `Missing store with alias ${aliasAndEmbeddedPageId.alias} for ${mgr.name} ${def.label} [${value}] prop!`,
                itemId,
              });
            } else {
              await store.initializeAttributes();
              const attr = store.getAttribute(value);
              if (!attr) {
                alerts.push({
                  key: `wrong-attr-${itemId}`,
                  showItemId: itemId,
                  alert: `${def.label} [${value}], used in ${mgr.name}, not found in data source ${store?.ds}!`,
                  itemId,
                });
              } else {
                const attrTypes = def.getAttributeDataTypes(_item);
                if (Array.isArray(attrTypes)) {
                  let attrType = attr.attributeType;
                  if (attrType === 'Reference' && attr.refMd) {
                    attrType = attr.refMd.attributeType;
                  }
                  if (!attrTypes.includes(attrType)) {
                    alerts.push({
                      key: `wrong-attr-type-${itemId}`,
                      showItemId: itemId,
                      alert: `${def.label} [${attr.attributeCode} of type ${
                        attr.attributeType
                      }], used in ${
                        mgr.name
                      }, has wrong data type! Expected one of ${attrTypes.join(
                        ', ',
                      )}`,
                      itemId,
                    });
                  }
                }
              }
            }
          }
        }
      }
    }),
  );
  if (alerts.length) {
    all_alerts.push(...alerts);
    all_autoFixes.push(...autoFixes);
  } else if (mgr.analyze) {
    const result = await mgr.analyze({
      platform,
      pageId,
      appUid,
      mgr,
      itemId: item.itemId,
      page,
    });
    if (result) {
      const [_alerts, _fixes] = result;
      all_alerts.push(..._alerts);
      all_autoFixes.push(..._fixes);
    }
  }
  await Promise.all(
    item.children.map((id) => {
      const child = page[id];
      if (child.parent !== item.itemId) {
        const fix = () => {
          const _page = { ...platform.getPage(appUid, pageId) };
          _page[id] = { ..._page[id], parent: item.itemId };
          platform.setter(pageAtom, _page, {
            params: { appUid, pageId },
          });
        };
        all_autoFixes.push(fix);
        all_alerts.push({
          key: `wrong-parent-${id}`,
          showItemId: id,
          alert: `Parent Item ID for [${id}] must be [${item.itemId}], instead found [${child.parent}]!`,
          itemId: id,
          fix,
        });
      }
      return analyzeItem({
        appUid,
        pageId,
        platform,
        page,
        item: child,
        alerts: all_alerts,
        autoFixes: all_autoFixes,
      });
    }),
  );
}

const AnalyzeResult = memo(
  ({
    appUid,
    pageId,
    platform,
    onClose,
    page,
  }: {
    appUid: string;
    pageId: string;
    platform: Platform;
    onClose: () => void;
    page: Page;
  }) => {
    const addToUndo = useStateXAddToUndo('io');
    const revertUndo = useStateXRevertUndo('io');
    const [autoFix, setAutoFix] = useStateXAtom(autoFixeModeAtom);
    const [[collect, autoFixes], setState] = useState<
      [JSX.Element[], (() => void)[]]
    >([[], []]);

    useEffect(() => {
      async function analyzePage() {
        const item = page.root;
        let _collect: AnalyzeIssue[] = [];
        let _autoFixes: (() => void)[] = [];
        try {
          await analyzeItem({
            appUid,
            pageId,
            platform,
            page,
            item,
            alerts: _collect,
            autoFixes: _autoFixes,
          });
          _collect = _collect.filter((c) => !!c);
          _autoFixes = _autoFixes.filter((c) => !!c);

          _collect.sort((a, b) => {
            const l = severityToInt(a.severity);
            const r = severityToInt(b.severity);
            if (l === r) {
              return 0;
            }
            return l < r ? -1 : 1;
          });

          const alerts = _collect.map((alert) =>
            makeAlert({ ...alert, platform, pageId, appUid }),
          );

          setState([alerts, _autoFixes]);
        } catch (e) {
          console.error(e);
          platform.showError(getErrorMessage(e));
        }
      }
      analyzePage();
    }, [appUid, page, pageId, platform]);

    useEffect(() => {
      const initialPage = platform.getPage(appUid, pageId);
      addToUndo(designPath({ appUid, pageId }), pageId);
      return () => {
        if (platform.getPage(appUid, pageId) === initialPage) {
          revertUndo(designPath({ appUid, pageId }), pageId);
        }
      };
    }, [addToUndo, appUid, pageId, platform, revertUndo]);

    useEffect(() => {
      platform.setter(hasAutoFixesAtom, autoFixes.length > 0);
      if (autoFix && autoFixes.length) {
        autoFixes.forEach((f) => f());
        setAutoFix(false);
      }
    }, [addToUndo, autoFix, autoFixes, platform, setAutoFix]);

    if (collect.length === 0) {
      return (
        <Alert
          sx={{ marginTop: 2 }}
          severity="success"
          action={
            <Button color="inherit" size="small" onClick={onClose}>
              Close
            </Button>
          }
        >
          Everything looks good!
        </Alert>
      );
    }
    return <>{collect}</>;
  },
);

export const hasAutoFixesAtom = atom({
  path: ['transient', 'hasAutoFixes'],
  defaultValue: true,
});

export const autoFixeModeAtom = atom({
  path: ['transient', 'autoFixeMode'],
  defaultValue: false,
});

export default function Analyze({
  appUid,
  pageId,
  platform,
  onClose,
}: {
  appUid: string;
  pageId: string;
  platform: Platform;
  onClose: () => void;
}) {
  const dragControls = useDragControls();
  const [, wh] = useWindowSize();
  const pageWidth = useStateXAtomValue(rootPageWidthAtom);

  function startDrag(event: React.PointerEvent<HTMLDivElement>) {
    dragControls.start(event);
  }

  const PaperComponent = useMemo(
    () => (props: PaperProps) =>
      <DraggablePaperComponent {...props} dragControls={dragControls} />,
    [dragControls],
  );

  return (
    <Dialog
      key="dialog"
      hideBackdrop
      disableEnforceFocus
      PaperComponent={PaperComponent}
      maxWidth="lg"
      open
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      style={{
        zIndex: zIndex.AnalyzeDialog,
      }}
      onMouseMove={stopPropagation}
      onMouseDown={stopPropagation}
      onMouseUp={stopPropagation}
      onClick={stopPropagation}
    >
      <DialogTitle
        key="title"
        onClick={stopPropagation}
        onPointerDown={startDrag}
        sx={{
          m: 0,
          p: 2,
          cursor: 'move',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        Page Analysis
        <Box
          sx={{
            display: 'inline-flex',
            fontSize: '0.8em',
            color: 'text.secondary',
            marginLeft: 2,
          }}
        >
          {pageId}
        </Box>
        <Box sx={{ flex: 1 }} />
        <AutoFix />
        <IconButton onClick={onClose}>
          <span aria-hidden="false" style={{ display: 'none' }}>
            Close
          </span>
          <Icon iconName="times" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ maxHeight: wh - 64 - 40, maxWidth: pageWidth }}>
        <AnalyzeInternal
          key={pageId}
          appUid={appUid}
          platform={platform}
          pageId={pageId}
          onClose={onClose}
        />
      </DialogContent>
    </Dialog>
  );
}

function AutoFix() {
  const hasAutoFixes = useStateXAtomValue(hasAutoFixesAtom);
  const setAutoFix = useStateXAtomSetter(autoFixeModeAtom);

  return hasAutoFixes ? (
    <IconButton
      data-tip="Auto Fix All"
      onClick={() => {
        setAutoFix(true);
      }}
    >
      <Icon iconName="wand" />
    </IconButton>
  ) : null;
}

const AnalyzeInternal = memo(
  ({
    appUid,
    pageId,
    platform,
    onClose,
  }: {
    appUid: string;
    pageId: string;
    platform: Platform;
    onClose: () => void;
  }) => {
    const page = useStateXAtomValue(pageAtom, { params: { appUid, pageId } });

    if (!page) {
      return null;
    }

    return (
      <AnalyzeResult
        key={pageId}
        appUid={appUid}
        platform={platform}
        pageId={pageId}
        onClose={onClose}
        page={page}
      />
    );
  },
);
